import React, {useEffect, useState} from 'react';
import {Button, Form, Message, Modal} from 'semantic-ui-react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {getFreshIdToken} from '../../utils/authUtil';

export type FeedbackReportTarget = {
  kind: 'comment' | 'reply';
  contentId: string;
  userId: string;
  displayName: string;
};

type Props = {
  bookId: string;
  target: FeedbackReportTarget | null;
  onClose: () => void;
};

const FeedbackReportModal: React.FC<Props> = ({bookId, target, onClose}) => {
  const {t} = useTranslation();
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setReason('');
    setDetails('');
    setError('');
    setBlocked(false);
  }, [target?.contentId]);

  const send = async (url: string, body?: object) => {
    const token = await getFreshIdToken();
    const response = await fetch(`${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}${url}`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json', 'br-token': token},
      body: body ? JSON.stringify(body) : undefined
    });
    if (!response.ok) {
      const result = await response.json().catch(() => ({}));
      throw new Error(result.msg || t('FailedToSubmitReport'));
    }
  };

  const act = async (action: 'report' | 'block') => {
    if (!target || busy) return;
    setBusy(true);
    setError('');
    try {
      if (action === 'report') {
        await send(`/books/${bookId}/feedback-reports`, {
          kind: target.kind, contentId: target.contentId, reason, comment: details
        });
        toast.info(t('ReportSubmitted'));
        onClose();
      } else {
        await send(`/books/${bookId}/users/${target.userId}/block-and-revoke`);
        toast.info(t('ReaderBlockedAndRevoked'));
        setBlocked(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('FailedToSubmitReport'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal size="small" open={!!target} onClose={onClose}>
      <Modal.Header>{t('ReportOrBlock')}</Modal.Header>
      <Modal.Content>
        <p>{t('ReportPrivacyNotice')}</p>
        <p>{target?.displayName}</p>
        {error && <Message error content={error} />}
        <Form>
          <Form.Select
            label={t('Reason')}
            placeholder={t('ChooseReportReason')}
            value={reason}
            options={['scam', 'spam', 'harassment', 'other'].map(value => ({
              key: value, value, text: t(`ReportReason_${value}`)
            }))}
            onChange={(event, data) => setReason(String(data.value))}
          />
          <Form.TextArea
            label={t('OptionalDetails')}
            maxLength={1000}
            value={details}
            onChange={(event, data) => setDetails(String(data.value))}
          />
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={busy}>{t('CancelReport')}</Button>
        <Button negative basic onClick={() => act('block')} loading={busy} disabled={busy || blocked}>
          {blocked ? t('ReaderBlockedAndRevoked') : t('BlockAndRevokeReader')}
        </Button>
        <Button negative onClick={() => act('report')} loading={busy} disabled={busy || !reason}>
          {t('SubmitReport')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default FeedbackReportModal;
